import { useMemo } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useMutation } from '@zenbusiness/application-commons-graphql';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';
import dayjs from 'dayjs';

import { FocusGrid, ContainerGrid, ActionButtonsGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import CardDetail from '#client/components/MoveMoney/components/SummaryFormComponents/CardDetail';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import TransactionDetail from '#client/components/MoveMoney/components/SummaryFormComponents/TransactionDetail';
import { convertCentToUSDText } from '#client/components/MoveMoney/utils/common';
import { DATE_FORMAT } from '#client/constants/date';
import { SUBJECT_TO_REVIEW_URL } from '#client/constants/FAQs';
import { TIME_LINE } from '#client/constants/transfer';
import useEnterSubmit from '#client/hooks/useEnterSubmit';
import useFormStep from '#client/hooks/useFormStep';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import { INSTANT_TRANSFER_MUTATION } from '#client/mutations';
import { capitalizeFirstLetter, formatAccountNumber } from '#client/utils/text';

import { DialogContentStyled } from './DialogContent.styles';
import { SummaryFormWrapperStyled } from './SummaryFormWrapper.styles';

import { MODAL_ACTION } from '../constants/instants';

export default function IsThisCorrectForm() {
  const { depositData, dispatchDepositData } = useFormStep();
  const [instantTransfer, { loading: instantTransferLoading }] =
    useMutation(INSTANT_TRANSFER_MUTATION);
  const authorizeButtonId = useEnterSubmit();

  const { selectedFromAccount, amount, toAccount, description, idempotencyKey } = depositData;
  const {
    uuid: cardUuid,
    cardHolderFirstName,
    cardHolderLastName,
    cardLastFourDigits,
    cardType
  } = selectedFromAccount;
  const fromCardHolder = [cardHolderFirstName, cardHolderLastName].join(' ');
  const fromType = capitalizeFirstLetter(cardType);
  const amountInUSD = convertCentToUSDText(amount);
  const fromCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits,
    formatCharacter: '•',
    numberFormatCharacter: 2
  });
  const initialDate = useMemo(() => dayjs(new Date()).format(DATE_FORMAT.MOVE_MONEY), []);
  const toName = toAccount?.name ?? '';

  const buttonPressed = useButtonPressedCallback({
    elementName: 'authorize_deposit_instantdep',
    elementText: 'Authorize',
    appPageName: 'authorize_transaction_Instantdep_modal'
  });

  const handleEditTransactionDetails = () => {
    dispatchDepositData({
      type: MODAL_ACTION.GO_TO_HOW_MUCH_FORM,
      payload: {
        isDisableBack: false
      }
    });
  };

  const handleSubmit = async () => {
    buttonPressed();

    const { data, error } = await instantTransfer({
      variables: {
        input: {
          sourceCardUuid: cardUuid,
          amount,
          idempotencyKey
        }
      }
    });

    // Go to Loading screen
    dispatchDepositData({
      type: error ? MODAL_ACTION.GO_TO_CONFIRMATION : MODAL_ACTION.GO_TO_LOADING_STEP,
      payload: {
        isDisableBack: true,
        initialDate,
        instantTransferRoutineUuid: data?.createInstantFundingTransfer?.uuid,
        createTransferError: error
      }
    });
  };

  const loadingSubmit = instantTransferLoading;

  useModalDisplayTracker({
    elementName: 'authorize_transaction_Instantdep_modal',
    elementText: 'authorize_transaction_Instantdep_modal',
    appPageName: 'active_banking_dashboard'
  });

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <SummaryFormWrapperStyled role="form">
              <Stack
                alignItems="center"
                paddingBottom={{ xs: '32px', lg: '40px' }}
              >
                <Heading
                  family="secondary"
                  size="3xl"
                  weight="bold"
                >
                  Is this correct?
                </Heading>
              </Stack>
              <Paper variant="outlined">
                <TransactionDetail
                  amount={amountInUSD}
                  dateInitiated={initialDate}
                  handleEdit={handleEditTransactionDetails}
                  hasEdit
                  timeline={TIME_LINE.INSTANT}
                  title="Deposit details"
                  transactionDescription={description}
                  transferType="Instant deposit"
                />
                <Divider />
                <RecipientDetail
                  accountHolder={toName}
                  accountNumber={toAccount?.accountNumber}
                  bank="ZenBusiness"
                  fullAccountNumber={toAccount?.accountNumber}
                  title="Recipient"
                />
                <Divider />
                <CardDetail
                  cardHolder={fromCardHolder}
                  cardNumber={fromCardNumber}
                  type={fromType}
                />
              </Paper>
              <Box marginTop={{ xs: '32px', lg: '40px' }}>
                <Text
                  color="tertiary"
                  size="sm"
                >
                  By selecting &quot;Authorize&quot; you allow ZenBusiness to debit the following
                  amount from your connected account on the date shown above. Transactions will be
                  processed immediately and are{' '}
                  <Link
                    href={SUBJECT_TO_REVIEW_URL}
                    size="sm"
                    target="_blank"
                    underline="always"
                  >
                    subject to review
                  </Link>
                  . Once authorized, no any changes or corrections can be made.
                </Text>
              </Box>
            </SummaryFormWrapperStyled>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          disabled={loadingSubmit}
          fullWidth
          id={authorizeButtonId}
          loading={loadingSubmit}
          onClick={handleSubmit}
          size="large"
          variant="contained"
        >
          Authorize
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}
